import { useState, useRef, useEffect } from 'react';
import { lockScroll, releaseScroll } from '../utils/scrollLock';
import { themeI18n } from '../utils/i18n_helper';
import { Accordion } from './Accordion';
import { PriceRangeSlider } from './PriceRangeSlider';
import { formatMoney } from '@shopify/theme-currency';
import '../../styles/components/accordion.scss'

import IconFilter from './IconFilter';
import IconClose from './IconClose';
import IconStar from './IconStar';

const url = new URL(window.location.href);

const validSortOptions = [
  "reviews",
  "relevancy",
  "a_to_z",
  "z_to_a",
  "price_min_to_max",
  "price_max_to_min",
  "creation_date_oldest",
  "creation_date",
  "popularity",
];

const listingData = JSON.parse(document.querySelector('[data-product-listing-data]').innerHTML);
const myStoreDataInitial = JSON.parse(localStorage.getItem('bentley_my_store'));

export const FiltersDrawer = ({ facets, sortBy, setSortBy, narrowBy, setNarrowBy, setPage, setPrevPage, setNextPage}) => {
  const [selectedFilters, setSelectedFilters] = useState(narrowBy);
  const [openedAccordions, setOpenedAccordions] = useState([]);
  const [myStoreData, setMyStoreData] = useState(myStoreDataInitial);

  const drawerDetails = useRef(null);

  const priceMaxFacet = facets && facets.find(([facetName]) => facetName === "Price_max");
  const priceMax = priceMaxFacet && priceMaxFacet[1][0] || Infinity;
  const locationsFacet = facets && facets.find(([facetName]) => facetName === "Locations");
  const filteredFacets = facets && facets.filter(([facetName]) => facetName !== "Price" && facetName !== "Price_min" && facetName !== "Locations");

  const drawerToggle = () => {
    if (drawerDetails.current.hasAttribute('open')) {
      drawerDetails.current.setAttribute('closing', '');
      releaseScroll();

      setTimeout(() => {
        drawerDetails.current.removeAttribute('open');
        drawerDetails.current.removeAttribute('closing');
        drawerDetails.current.setAttribute('closed', '');
      }, 500);
    } else {
      drawerDetails.current.removeAttribute('closed');
      drawerDetails.current.setAttribute('open', '');
      lockScroll();
    }
  }

  const handleFilterChange = (facetName, value) => {
    let newFilters = [];
    if (facetName === 'Price_from_to') {
      const [minPriceValue, maxPriceValue] = value;
      const priceRangeValue = `${minPriceValue}-${maxPriceValue}`;
      newFilters = selectedFilters.filter(filter => filter[0] !== facetName);
      newFilters = [...newFilters, [facetName, priceRangeValue]];
      const filterFacetName = `filter.${facetName}`;
      setUrlParam(filterFacetName, priceRangeValue);

      // check if min price is 0 and max price is maximum
      if ((minPriceValue == 0) && (maxPriceValue == priceMax && priceMax)) {
        newFilters = selectedFilters.filter(filter => filter[0] !== facetName);
        deleteUrlParam(filterFacetName);
      }
    } else {
      const existingFilterIndex = selectedFilters.findIndex(filter => filter[0] === facetName && filter[1] === value);
      if (existingFilterIndex !== -1) {
        newFilters = [...selectedFilters];
        newFilters.splice(existingFilterIndex, 1);
        removeFilterUrlParam(facetName, value);
      } else {
        newFilters = [...selectedFilters, [facetName, value]];
        setFilterUrlParam(facetName, value);
      }
    }

    resetPage();
    setSelectedFilters(newFilters);
    setNarrowBy(newFilters);
  };

  const setUrlParam = (paramName, paramValue) => {
    url.searchParams.set(paramName, paramValue);
    window.history.pushState(null, null, decodeURIComponent(url.toString()));
  }

  const deleteUrlParam = (paramName) => {
    url.searchParams.delete(paramName);
    window.history.pushState(null, null, decodeURIComponent(url.toString()));
  }

  const setFilterUrlParam = (paramName, paramValue, refresh = true) => {
    const filterParamName = `filter.${paramName}`;
    const existingParams = url.searchParams.getAll(filterParamName);
    if(existingParams.length > 0) {
      url.searchParams.delete(filterParamName);
      existingParams.forEach(existingValue => {
        url.searchParams.append(filterParamName, existingValue);
      });
    }
    url.searchParams.append(filterParamName, paramValue);
    if (refresh) {
      window.history.pushState(null, null, decodeURIComponent(url.toString()));
    }
  }

  const removeFilterUrlParam = (paramName, paramValue, refresh = true) => {
    const filterParamName = `filter.${paramName}`;
    const existingParams = url.searchParams.getAll(filterParamName);
    if(existingParams.length > 0) {
      const filteredParams = existingParams.filter(value => value !== paramValue);
      url.searchParams.delete(filterParamName);
      filteredParams.forEach(existingValue => {
        url.searchParams.append(filterParamName, existingValue);
      });
    }
    if(refresh) {
      window.history.pushState(null, null, decodeURIComponent(url.toString()));
    }
  }

  const RenderStarsRating = ({ rating }) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      if (i < rating) {
        stars.push(<IconStar filled={true} key={i} />);
      } else {
        stars.push(<IconStar filled={false} key={i} />);
      }
    }
    return (
      <span className="product-listing__stars-rating">{stars}</span>
    );
  }

  const RenderFilterLabel = ({ optionValue, categoryTitle, count, isRating, isCategories }) => {
    if (isRating) {
      return (
        <>
          <RenderStarsRating rating={optionValue} />
        </>
      );
    } else if (isCategories) {
      return (
        <>
          {categoryTitle} {`(${count})`}
        </>
      );
    } else {
      return (
        <>
          {optionValue} {`(${count})`}
        </>
      );
    }
  }

  const RenderPriceMaxFacet = () => {
    if (!priceMaxFacet) return null;

    return (
      <Accordion title={themeI18n('shopify.filters.price')} setOpenedAccordions={setOpenedAccordions} openedAccordions={openedAccordions}>
        <PriceRangeSlider
          priceMax={priceMax}
          selectedFilters={selectedFilters}
          handleFilterChange={handleFilterChange}
        />
      </Accordion>
    );
  };

  const RenderFilteredFacets = () => {
    return filteredFacets.map(([facetName, options, label, isRange, sortOrder]) => {
      if (!isRange) {
        if (facetName.toLowerCase() === 'color' || facetName.toLowerCase() === 'couleur') {
          // TODO - swatches?
        }

        return (
          <Accordion key={facetName} title={label} setOpenedAccordions={setOpenedAccordions} openedAccordions={openedAccordions}>
            {options.map(([optionValue, count, categoryTitle]) => {
              const isSelectedFilter = selectedFilters.some(filter => filter[0] === facetName && filter[1] === optionValue);
              const isCategories = facetName.toLowerCase() === 'categories';
              const isRating = facetName.toLowerCase() === 'isp-review-stars';

              return (
                <label key={optionValue} className="product-listing__facet-checkbox checkbox">
                  <input
                    type="checkbox"
                    name={facetName}
                    value={optionValue}
                    onChange={(e) => handleFilterChange(facetName, e.target.value)}
                    checked={isSelectedFilter}
                  />
                  <div className="checkbox__box"></div>
                  <span className="product-listing__facet-option checkbox__label">
                    <RenderFilterLabel optionValue={optionValue} categoryTitle={categoryTitle} count={count} isRating={isRating} isCategories={isCategories} />
                  </span>
                </label>
              );
            })}
          </Accordion>
        );
      }
    });
  };

  const RenderLocationsFacet = () => {
    if (!myStoreData) {
      return;
    }

    const myStoreFacet = locationsFacet && locationsFacet[1].find(([locationName, count]) => locationName.includes(myStoreData.store_number));

    if (myStoreFacet) {
      return (
        <div className='product-listing__location-filter'>
          <label className="product-listing__facet-checkbox checkbox">
            <input
              type="checkbox"
              name="Locations"
              value={myStoreFacet[0]}
              onChange={(e) => handleFilterChange('Locations', e.target.value)}
              checked={selectedFilters.some(filter => filter[0] === 'Locations' && filter[1] === myStoreFacet[0])}
            />
            <div className="checkbox__box"></div>

            <span className="product-listing__facet-option checkbox__label">
              {themeI18n('shopify.filters.availability.available_at', {location: myStoreData.name})}
            </span>
          </label>
        </div>
      )
    }
  }

  const removeUrlParamsWithPrefix = (prefix) => {
    const paramsToDelete = [];
    url.searchParams.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        paramsToDelete.push(key);
      }
    });
    paramsToDelete.forEach(param => {
      url.searchParams.delete(param);
    });
    window.history.pushState(null, null, decodeURIComponent(url.toString()));
  };

  const clearAllFilters = () => {
    setSelectedFilters([]);
    setNarrowBy([]);
    removeUrlParamsWithPrefix('filter');
    resetPage();
  }

  const removeFilter = (facetName, value) => {
    const newFilters = selectedFilters.filter(filter => filter[0] !== facetName || filter[1] !== value);
    setSelectedFilters(newFilters);
    setNarrowBy(newFilters);
    removeFilterUrlParam(facetName, value);
    resetPage();
  }

  const getCategoryTitleById = (categoryId) => {
    const categoriesFacet = facets.find(([facetName]) => facetName === "Categories");
    if (categoriesFacet) {
      const category = categoriesFacet[1].find(([id]) => id === categoryId);
      if (category) {
        return category[2];
      }
    }
    return null;
  };

  const resetPage = () => {
    setPrevPage(0);
    setNextPage(2);
    setPage(1);

    url.searchParams.set('page', 1);
    window.history.pushState(null, null, url.toString());
  }

  useEffect(() => {
    document.addEventListener('myStoreUpdated', () => {
      const newStoreData = JSON.parse(localStorage.getItem('bentley_my_store'));

      setMyStoreData(newStoreData);
    });

    return () => {
      document.removeEventListener('myStoreUpdated', () => {
        const newStoreData = JSON.parse(localStorage.getItem('bentley_my_store'));

        setMyStoreData(newStoreData);
      });
    }
  });

  return (
    <>
      <button
        className={(listingData.enableSort && !listingData.enableFilters) ? "product-listing__filter-toggle hide-desktop-up" : "product-listing__filter-toggle"}
        onClick={drawerToggle}
      >
        {(listingData.enableSort && listingData.enableFilters) ? (
          <>
            <span className='hide-desktop-down'>{themeI18n('collection.filters.title')}</span>
            <span className='hide-desktop-up'>{themeI18n('collection.filters.title_with_sort')}</span>
          </>
        ): (
          <>
            {listingData.enableSort && (
              <span>{themeI18n('collection.filters.sort_title')}</span>
            )}

            {listingData.enableFilters && (
              <span>{themeI18n('collection.filters.title')}</span>
            )}
          </>
        )}
        <IconFilter />
      </button>

      <div className='drawer'>
        <div className='drawer__details' ref={drawerDetails}>
          <div className='drawer__inner drawer__inner--right' >
            <div className='drawer__inner-background' onClick={drawerToggle}/>
            <div className='drawer__inner-content-wrapper color-main'>
              <div className='drawer__header h4'>
                <button className="drawer__close-button" onClick={drawerToggle} aria-label={themeI18n('accessibility.close')}>
                  <IconClose />
                </button>

                <div className="drawer__header-content">
                  <div className="drawer__header-title-with-icon">
                    {listingData.enableFilters ? (
                      <>{themeI18n('collection.filters.title')}</>
                    ) : (
                      <>{themeI18n('collection.filters.sort_title')}</>
                    )}
                    <IconFilter />
                  </div>
                </div>
              </div>

              <div className='drawer__inner-content product-listing__filters-drawer-content'>
                {listingData.enableSort && (
                  <div className='product-listing__sort-by-mobile hide-desktop-up'>
                    <p className='product-listing__sort-by-mobile-title'>
                      {themeI18n('collection.sort_by_label')}
                    </p>

                    <div className='product-listing__sort-by-mobile-options'>
                      {validSortOptions.map((option) => {
                        return (
                          <label key={option} className="product-listing__facet-checkbox checkbox">
                            <input
                              type="checkbox"
                              value={option}
                              onChange={(e) => {
                                url.searchParams.set('sort_by', option);
                                setSortBy(option);
                                resetPage();
                              }}
                              checked={sortBy === option ? true : false}
                            />
                            <div className="checkbox__box"></div>
                            <span className="product-listing__facet-option checkbox__label">
                              {themeI18n(`shopify.collections.sorting_fs.${option}`)}
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                )}

                {listingData.enableFilters && (
                  <>
                    {selectedFilters.length > 0 && (
                      <div className='product-listing__selected-filters'>
                        <div className='product-listing__selected-filters-title-wrapper'>
                          <span className='product-listing__selected-filters-title'>{themeI18n('collection.filters.title')} ({selectedFilters.length})</span>
                          <button onClick={clearAllFilters} className='product-listing__selected-filters-clear'>{themeI18n('collection.filters.clear_all')}</button>
                        </div>

                        <div className='product-listing__selected-filters-list'>
                          {selectedFilters.map(([facetName, facetValue]) => {
                            let value = getCategoryTitleById(facetValue) || facetValue;

                            if (facetName === 'Price_from_to') {
                              const minPriceValue = facetValue.split("-")[0];
                              const minPriceValueCurrency = formatMoney(parseFloat(minPriceValue * 100), "{{amount_with_comma_separator}}$");
                              const maxPriceValue = facetValue.split("-")[1];
                              const maxPriceValueCurrency = formatMoney(parseFloat(maxPriceValue * 100), "{{amount_with_comma_separator}}$");
                              value = `${minPriceValueCurrency}-${maxPriceValueCurrency}`;
                            }

                            if (facetName === 'Isp-review-stars') {
                              value = `${value} ${themeI18n('collection.filters.stars')}`;
                            }

                            return (
                              <button onClick={()=> removeFilter(facetName, facetValue)} key={facetName + facetValue} className='product-listing__selected-filter'>
                                <span className='product-listing__selected-filter-name'>{value}</span>
                                <span className='product-listing__selected-filter-remove'><IconClose /></span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <RenderLocationsFacet />
                    <RenderPriceMaxFacet />
                    <RenderFilteredFacets />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
};
